/**
 * renameEngine.js — isolierter API-Client für das neue Rename-Engine-System.
 *
 * Bewusst eigenständig: spricht NUR die /rename-engine/* Endpunkte an und
 * teilt mit dem restlichen Frontend nur die API_BASE. Keine Kopplung an die
 * alten Rename-Seiten.
 */
import { API_BASE } from "./config";

const PREFIX = `${API_BASE}/rename-engine`;

/** URL der rohen PDF-Bytes aus Dropbox — wird vom FloatingPdfViewer geladen. */
export function dropboxFileUrl(path) {
  return `${PREFIX}/dropbox/file?path=${encodeURIComponent(path)}`;
}

async function getJson(path, params) {
  const query = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await fetch(`${PREFIX}${path}${query}`);
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (data && data.detail) || res.statusText;
    throw new Error(msg);
  }
  return data;
}

export const renameEngineApi = {
  health: () => getJson("/health"),
  status: () => getJson("/status"),
  catalogsSummary: () => getJson("/catalogs/summary"),
  dropboxList: (folder, recursive = false) =>
    getJson("/dropbox/list", { folder, recursive: recursive ? "true" : "false" }),
  dropboxBrowse: (folder = "") =>
    getJson("/dropbox/browse", { folder }),

  analyze: async (file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${PREFIX}/analyze`, { method: "POST", body: form });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },

  analyzeDropbox: async (path) => {
    const res = await fetch(`${PREFIX}/analyze/dropbox`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },

  // --- Training / Gold ---------------------------------------------------
  goldSave: async ({ sha256, filename, source_path, gold, schema }) => {
    const res = await fetch(`${PREFIX}/gold/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sha256, filename, source_path, gold, schema }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },

  goldForSha: (sha256) =>
    getJson("/gold/for-sha", { sha256 }),

  // Lernen: speichert Gold UND bindet sichere Felder (prefix/sender/schema) an
  // dokument-eigene Anker, sodass künftige Dokumente automatisch korrekt
  // benannt werden.
  learn: async ({ sha256, corrected, schema, filename, name_segments }) => {
    const res = await fetch(`${PREFIX}/learn`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sha256, corrected, schema, filename, name_segments }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },

  /** Welle 15: Live-Vorschau, wie ein Filename in Felder zerlegt wird. */
  parseFilename: async ({ sha256, filename, schema }) => {
    const res = await fetch(`${PREFIX}/parse-filename`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sha256, filename, schema }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error((data && data.detail) || res.statusText);
      err.status = res.status;
      throw err;
    }
    return data;
  },

  /** Welle 15: Schema-Dropdown-Quelle. */
  schemasList: () => getJson("/schemas/list"),

  /** Welle 18: Stammdaten-Cache neu laden (nach DB-Änderung im Admin). */
  catalogsReload: async () => {
    const res = await fetch(`${PREFIX}/catalogs/reload`, { method: "POST" });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },

  dropboxRename: async ({ source_path, new_name, autorename = false }) => {
    const res = await fetch(`${PREFIX}/dropbox/rename`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_path, new_name, autorename }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error((data && data.detail) || res.statusText);
      err.status = res.status;
      throw err;
    }
    return data;
  },

  goldList: (limit = 200) =>
    getJson("/gold/list", { limit }),

  trainFromGold: async (min_samples = 10) => {
    const res = await fetch(
      `${PREFIX}/eval/train-from-gold?min_samples=${min_samples}&note=ui`,
      { method: "POST" }
    );
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },

  reloadWeights: async () => {
    const res = await fetch(`${PREFIX}/eval/reload-weights`, { method: "POST" });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || res.statusText);
    return data;
  },
};

export default renameEngineApi;
